import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { getRandomNumber } from 'src/shared/utils/get-random-number';

import { RigOperationSection } from './rig-operation-section.entity';
import { isStageField } from './rig-operations-service';
import { TRawRigOperationSection, TRawRigOperationStage } from './types';

export type TStageData = {
  startDate: number | null;
  endDate: number | null;
  duration: number | null;
  wellLifeCycleStageId: number | null;
  passingValue: number | null;
  distance: number | null;
  commercialSpeed: number | null;
  index: number;
};

type TErrors = Partial<Record<keyof TStageData, string>>;

export const STAGE_DATA_FIELDS: string[] & (keyof TStageData)[] = [
  'startDate',
  'endDate',
  'duration',
  'wellLifeCycleStageId',
  'distance',
  'passingValue',
  'index',
  'commercialSpeed',
];

export class RigOperationStage {
  readonly entityId = getRandomNumber();
  readonly initialValues: Partial<TRawRigOperationStage>;
  readonly initialSections: TRawRigOperationSection[];
  readonly initialRawStage?: Partial<TRawRigOperationStage>;

  @observable private _errors: TErrors = {};
  @observable private _data: TStageData;
  @observable sections: RigOperationSection[] = [];
  readonly sectionsMap = observable.map<number, RigOperationSection>();

  constructor(index: number, initialValue?: Partial<TRawRigOperationStage>) {
    this._data = {
      startDate: initialValue?.startDate ?? null,
      endDate: initialValue?.endDate ?? null,
      duration: initialValue?.duration ?? null,
      wellLifeCycleStageId: initialValue?.wellLifeCycleStageId ?? null,
      passingValue: initialValue?.passingValue ?? null,
      distance: initialValue?.distance ?? null,
      commercialSpeed: initialValue?.commercialSpeed ?? null,
      index,
    };
    this.initialValues = {
      startDate: initialValue?.startDate,
      endDate: initialValue?.endDate,
      duration: initialValue?.duration,
      wellLifeCycleStageId: initialValue?.wellLifeCycleStageId,
      passingValue: initialValue?.passingValue,
      distance: initialValue?.distance,
      commercialSpeed: initialValue?.commercialSpeed,
      index,
    };
    this.initialSections = initialValue?.sections ?? [];
    this.initialRawStage = initialValue;

    makeObservable(this);
  }

  get data(): TStageData {
    return toJS(this._data);
  }

  @computed
  get errors(): TErrors {
    return toJS(this._errors);
  }

  @action.bound
  clearErrors(): void {
    this._errors = {};
  }

  @action.bound
  addError(fieldId: keyof TStageData, errorText: string): void {
    this._errors = { ...this._errors, [fieldId]: errorText };
  }

  @action.bound
  removeError(fieldId: keyof TStageData): void {
    const errors: TErrors = {};

    for (const key in this._errors) {
      if (key === fieldId || !isStageField(key)) {
        continue;
      }

      errors[key] = this._errors[key];
    }

    this._errors = errors;
  }

  @action.bound
  setSections(sections: RigOperationSection[]): void {
    this.sections = sections;

    this.sectionsMap.clear();
    sections.forEach((s) => this.sectionsMap.set(s.entityId, s));
  }

  @computed
  get startDate(): number | null {
    return this._data.startDate;
  }

  @action.bound
  setStartDate(value: number | null): void {
    this._data = { ...this._data, startDate: value };
  }

  @computed
  get endDate(): number | null {
    return this._data.endDate;
  }

  @action.bound
  setEndDate(value: number | null): void {
    this._data = { ...this._data, endDate: value };
  }

  @computed
  get duration(): number | null {
    return this._data.duration;
  }

  @action.bound
  setDuration(value: number | null): void {
    this._data = { ...this._data, duration: value };
  }

  @computed
  get wellLifeCycleStageId(): number | null {
    return this._data.wellLifeCycleStageId;
  }

  @action.bound
  setWellLifeCycleStageId(value: number | null): void {
    this._data = { ...this._data, wellLifeCycleStageId: value };
  }

  @computed
  get passingValue(): number | null {
    return this._data.passingValue;
  }

  @action.bound
  setPassingValue(value: number | null): void {
    this._data = { ...this._data, passingValue: value };
  }

  @computed
  get commercialSpeed(): number | null {
    return this._data.commercialSpeed;
  }

  @action.bound
  setCommercialSpeed(value: number | null): void {
    this._data = { ...this._data, commercialSpeed: value };
  }

  @computed
  get distance(): number | null {
    return this._data.distance;
  }

  @action.bound
  setDistance(value: number | null): void {
    this._data = { ...this._data, distance: value };
  }
}
